import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  IconButton,
  Button,
  Typography,
  Paper,
  Toolbar,
  Grid,
  Tooltip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton as MuiIconButton,
} from '@mui/material';
import {
  ZoomIn,
  ZoomOut,
  FitScreen,
  Download,
  NavigateBefore,
  NavigateNext,
  RotateRight,
  MenuBook,
  Close,
} from '@mui/icons-material';
import { colors } from '../../theme';
import TableOfContents from './TableOfContents';

/**
 * Lightweight Page Image Viewer
 * Loads individual page images from the backend instead of the entire PDF
 * This dramatically reduces initial load time from 30MB+ to ~200KB per page
 */
const PDFViewer = ({ pdfUrl, issueTitle, initialPage = 1, pages = [], totalPages = 0 }) => {
  const { t } = useTranslation();
  const [pageNumber, setPageNumber] = useState(initialPage);
  const [scale, setScale] = useState(1.0);
  const [rotation, setRotation] = useState(0);
  const [loading, setLoading] = useState(true);
  const [imageError, setImageError] = useState(false);
  const [contentDialogOpen, setContentDialogOpen] = useState(false);

  const numPages = totalPages || pages.length;

  // Get current page data
  const currentPage = pages.find(p => p.page_number === pageNumber);
  const currentImageUrl = currentPage?.image;

  // Update page number if initialPage changes
  useEffect(() => {
    if (initialPage && initialPage !== pageNumber) {
      setPageNumber(initialPage);
    }
  }, [initialPage]);

  // Reset loading state when page changes
  useEffect(() => {
    setLoading(true);
    setImageError(false);
  }, [pageNumber]);

  const handleImageLoad = useCallback(() => {
    setLoading(false);
  }, []);

  const handleImageError = useCallback(() => {
    setLoading(false);
    setImageError(true);
  }, []);

  const changePage = (offset) => {
    setPageNumber((prevPageNumber) => {
      const newPageNumber = prevPageNumber + offset;
      return Math.max(1, Math.min(newPageNumber, numPages));
    });
  };

  const previousPage = () => changePage(-1);
  const nextPage = () => changePage(1);

  const zoomIn = () => setScale((prev) => Math.min(prev + 0.2, 3.0));
  const zoomOut = () => setScale((prev) => Math.max(prev - 0.2, 0.5));
  const resetScale = () => setScale(1.0);

  const rotateClockwise = () => setRotation((prev) => (prev + 90) % 360);

  const handleDownload = () => {
    if (pdfUrl) {
      const link = document.createElement('a');
      link.href = pdfUrl;
      link.download = `${issueTitle || 'magazine'}.pdf`;
      link.click();
    }
  };

  const goToPage = (page) => {
    setPageNumber(page);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Toolbar */}
      <Paper elevation={2} sx={{ mb: 2 }}>
        <Toolbar
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 1,
            py: 1,
          }}
        >
          {/* Page Navigation */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Tooltip title={t('previous')}>
              <span>
                <IconButton
                  onClick={previousPage}
                  disabled={pageNumber <= 1}
                  size="small"
                >
                  <NavigateBefore />
                </IconButton>
              </span>
            </Tooltip>
            <Typography variant="body2" sx={{ minWidth: 100, textAlign: 'center' }}>
              {t('page')} {pageNumber} {t('of')} {numPages || '--'}
            </Typography>
            <Tooltip title={t('next')}>
              <span>
                <IconButton
                  onClick={nextPage}
                  disabled={pageNumber >= numPages}
                  size="small"
                >
                  <NavigateNext />
                </IconButton>
              </span>
            </Tooltip>
          </Box>

          {/* Zoom Controls */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Tooltip title={t('zoomOut')}>
              <IconButton onClick={zoomOut} size="small" disabled={scale <= 0.5}>
                <ZoomOut />
              </IconButton>
            </Tooltip>
            <Typography variant="body2" sx={{ minWidth: 50, textAlign: 'center' }}>
              {Math.round(scale * 100)}%
            </Typography>
            <Tooltip title={t('zoomIn')}>
              <IconButton onClick={zoomIn} size="small" disabled={scale >= 3.0}>
                <ZoomIn />
              </IconButton>
            </Tooltip>
            <Tooltip title={t('fitPage')}>
              <IconButton onClick={resetScale} size="small">
                <FitScreen />
              </IconButton>
            </Tooltip>
          </Box>

          {/* Action Buttons */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Tooltip title={t('tableOfContents')}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<MenuBook />}
                onClick={() => setContentDialogOpen(true)}
                sx={{
                  borderColor: colors.royalBlue,
                  color: colors.royalBlue,
                  '&:hover': {
                    borderColor: colors.royalBlue,
                    backgroundColor: 'rgba(0, 51, 141, 0.08)',
                  },
                }}
              >
                {t('content')}
              </Button>
            </Tooltip>
            <Tooltip title={t('rotate')}>
              <IconButton onClick={rotateClockwise} size="small">
                <RotateRight />
              </IconButton>
            </Tooltip>
            {pdfUrl && (
              <Tooltip title={t('download')}>
                <IconButton onClick={handleDownload} size="small">
                  <Download />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Toolbar>
      </Paper>

      {/* Page Viewer with Table of Contents Sidebar */}
      <Grid container spacing={2} sx={{ flexGrow: 1 }}>
        {/* Table of Contents - Left sidebar */}
        <Grid item xs={12} md={3} sx={{ display: { xs: 'none', md: 'block' } }}>
          <Box sx={{ height: '70vh' }}>
            <TableOfContents
              pages={pages}
              currentPage={pageNumber}
              onPageClick={goToPage}
            />
          </Box>
        </Grid>

        {/* Main Page View */}
        <Grid item xs={12} md={9}>
          <Paper
            elevation={3}
            sx={{
              height: '70vh',
              overflow: 'auto',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: '#525659',
              p: 2,
              position: 'relative',
            }}
          >
            {loading && (
              <Box sx={{ 
                position: 'absolute', 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center',
                zIndex: 10,
              }}>
                <CircularProgress sx={{ color: 'white' }} />
              </Box>
            )}
            
            {imageError ? (
              <Box sx={{ textAlign: 'center', color: 'white' }}>
                <Typography variant="h6" gutterBottom>
                  {t('errorLoadingPage') || 'Unable to load page'}
                </Typography>
                <Typography variant="body2">
                  Page {pageNumber}
                </Typography>
              </Box>
            ) : currentImageUrl ? (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  width: '100%',
                  height: '100%',
                }}
              >
                <img
                  src={currentImageUrl}
                  alt={`Page ${pageNumber}`}
                  onLoad={handleImageLoad}
                  onError={handleImageError}
                  style={{
                    maxWidth: '100%',
                    maxHeight: '100%',
                    objectFit: 'contain',
                    transform: `scale(${scale}) rotate(${rotation}deg)`,
                    transformOrigin: 'center center',
                    transition: 'transform 0.2s ease',
                    opacity: loading ? 0.3 : 1,
                  }}
                />
              </Box>
            ) : (
              <Box sx={{ textAlign: 'center', color: 'white' }}>
                <Typography variant="body1">
                  {t('noPageAvailable') || 'No page image available'}
                </Typography>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Content Dialog - Full Page Directory */}
      <Dialog
        open={contentDialogOpen}
        onClose={() => setContentDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            minHeight: '80vh',
            maxHeight: '90vh',
          },
        }}
      >
        <DialogTitle
          sx={{
            backgroundColor: colors.royalBlue,
            color: 'white',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            py: 2,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <MenuBook />
            <Typography variant="h6" component="span">
              {t('tableOfContents')}
            </Typography>
          </Box>
          <IconButton
            onClick={() => setContentDialogOpen(false)}
            sx={{ color: 'white' }}
            size="small"
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0, height: '100%' }}>
          <Box sx={{ height: '100%', overflow: 'auto' }}>
            <TableOfContents
              pages={pages}
              currentPage={pageNumber}
              onPageClick={(page) => {
                goToPage(page);
                setContentDialogOpen(false);
              }}
              simplified={true}
            />
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default PDFViewer;